/**
 * GetLabsAdmin.tsx
 *
 * Admin → Integrations → GetLabs. Configure the GetLabs connection (API hook-up
 * status), set the per-account billing mode + price, and track the month-end
 * payout reconciliation.
 *
 * Locked (plan 2026-10-10-001 U7: R8, R18): the price, billing mode and
 * covered memberships show read-only (an AdminLockedFieldset; the billing
 * mode as its label), with no Save and no membership remove; the
 * reconciliation still reads. Nothing says why.
 */
import { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, X } from 'lucide-react';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from 'recharts';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardBody } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Spinner } from '@/components/ui';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { RadioGroup } from '@/components/ui/RadioGroup';
import { SearchableMultiSelect } from '@/components/ui/SearchableMultiSelect';
import { useToast } from '@/hooks/useToast';
import { getErrorMessage } from '@/services/apiClient';
import { AdminLockedFieldset } from '@/components/admin/AdminLockedFieldset';
import { useAdminPageLevel } from '@/components/admin/AdminPageLevelContext';
import {
  getEnabledIntegrations,
  getIntegrationSettings,
  updateIntegrationSettings,
  getIntegrationReconciliation,
} from '@/services/integrations.service';
import { listTemplates } from '@/services/memberships.service';

const KEY = 'getlabs';

const BILLING_MODE_LABELS: Record<string, string> = {
  charge_patient: 'Charge the patient',
  log_for_settlement: 'Log & settle monthly',
};
const usd = (n: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);

export function GetLabsAdmin() {
  const locked = useAdminPageLevel() === 'locked';
  const toast = useToast();
  const qc = useQueryClient();

  const { data: integrations = [], isLoading: enabledLoading } = useQuery({
    queryKey: ['integrations', 'enabled'],
    queryFn: getEnabledIntegrations,
    staleTime: 60_000,
  });
  const enabled = useMemo(() => integrations.find((i) => i.key === KEY), [integrations]);

  const { data: settings } = useQuery({
    queryKey: ['integration-settings', KEY],
    queryFn: () => getIntegrationSettings(KEY),
    enabled: !!enabled,
    staleTime: 60_000,
  });

  const { data: recon, isLoading: reconLoading } = useQuery({
    queryKey: ['integration-reconciliation', KEY],
    queryFn: () => getIntegrationReconciliation(KEY, 6),
    enabled: !!enabled,
    staleTime: 60_000,
  });

  const saveMode = useMutation({
    mutationFn: (billingMode: string) => updateIntegrationSettings(KEY, { billingMode }),
    onSuccess: () => { toast.success('Billing mode updated.'); qc.invalidateQueries({ queryKey: ['integration-settings', KEY] }); },
    onError: (err) => toast.error(getErrorMessage(err, 'Failed to update billing mode.')),
  });

  const [priceInput, setPriceInput] = useState('');
  useEffect(() => { if (settings) setPriceInput(String(settings.price)); }, [settings]);

  const savePrice = useMutation({
    mutationFn: (price: number) => updateIntegrationSettings(KEY, { price }),
    onSuccess: () => {
      toast.success('Price updated.');
      qc.invalidateQueries({ queryKey: ['integration-settings', KEY] });
      qc.invalidateQueries({ queryKey: ['integrations', 'enabled'] });
    },
    onError: (err) => toast.error(getErrorMessage(err, 'Failed to update price.')),
  });

  const priceDirty = settings != null && priceInput.trim() !== '' && Number(priceInput) !== settings.price;
  // Super-admin floor (Prospera-Corp-App). 0 = no minimum. The server also
  // enforces this; we block the Save + show the floor so it's obvious.
  const minPrice = settings?.minCustomerPrice ?? 0;
  const belowMin = settings != null && priceInput.trim() !== '' && minPrice > 0 && Number(priceInput) < minPrice;

  // Membership templates → multi-select. Saving updates entitledMembershipIds.
  const { data: templates = [] } = useQuery({
    queryKey: ['membership-templates'],
    queryFn: () => listTemplates(),
    enabled: !!enabled,
    staleTime: 5 * 60_000,
  });

  const membershipOptions = useMemo(
    () => templates.filter((t) => t.active).map((t) => ({ value: String(t.templateId), label: t.title })),
    [templates],
  );

  // Locally tracked entitlement IDs so the checkbox updates instantly on click,
  // independent of the server round-trip. Sync from settings whenever it loads
  // or is invalidated.
  const [entitledLocal, setEntitledLocal] = useState<string[]>([]);
  useEffect(() => {
    if (settings) setEntitledLocal((settings.entitledMembershipIds ?? []).map(String));
  }, [settings]);

  const saveEntitled = useMutation({
    mutationFn: (ids: number[]) => updateIntegrationSettings(KEY, { entitledMembershipIds: ids }),
    onSuccess: () => {
      toast.success('Covered memberships updated.');
      qc.invalidateQueries({ queryKey: ['integration-settings', KEY] });
    },
    onError: (err) => {
      toast.error(getErrorMessage(err, 'Failed to update memberships.'));
      // Revert local state to the server's last known value.
      if (settings) setEntitledLocal((settings.entitledMembershipIds ?? []).map(String));
    },
  });

  const billingOptions = useMemo(
    () => (settings?.billingModes ?? []).map((m) => ({ value: m, label: BILLING_MODE_LABELS[m] ?? m })),
    [settings],
  );

  const totals = useMemo(() => {
    const months = recon?.months ?? [];
    return {
      owed: months.reduce((s, m) => s + m.owedToVendor, 0),
      collected: months.reduce((s, m) => s + m.collected, 0),
      visits: months.reduce((s, m) => s + m.visits, 0),
    };
  }, [recon]);

  if (enabledLoading) {
    return <div className="flex justify-center py-16"><Spinner /></div>;
  }

  if (!enabled) {
    return (
      <div className="space-y-6 p-6">
        <PageHeader title="GetLabs" subtitle="At-home lab draws via the GetLabs partner API" />
        <Card>
          <CardBody>
            <div className="flex flex-col items-center gap-2 py-10 text-center">
              <AlertTriangle className="h-8 w-8 text-amber-400" />
              <p className="text-sm text-slate-600">
                GetLabs is not enabled for this account. Please contact your Prospera account team to add this
                feature to your account.
              </p>
            </div>
          </CardBody>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title="GetLabs"
        subtitle="At-home lab draws via the GetLabs partner API"
      />

      <AdminLockedFieldset>
      {/* Price */}
      <Card>
        <CardBody>
          <h3 className="text-sm font-semibold text-slate-900">Price</h3>
          <p className="text-[11px] text-slate-400 mb-2">
            Charged to the patient (charge mode) or accrued for settlement.
          </p>
          <label className="block text-xs font-medium text-slate-600 mb-1">Amount ($)</label>
          <div className="flex items-center gap-2">
            <div className="flex w-40 items-stretch">
              <span className="inline-flex items-center rounded-l-md border border-r-0 border-slate-300 bg-slate-100 px-2.5 text-sm font-medium text-slate-500">
                $
              </span>
              <Input
                type="number"
                min={minPrice > 0 ? String(minPrice) : '0'}
                step="0.01"
                value={priceInput}
                onChange={(e) => setPriceInput(e.target.value)}
                className="rounded-l-none"
              />
            </div>
            {!locked && (
              <Button size="sm" onClick={() => savePrice.mutate(Number(priceInput))} disabled={!priceDirty || belowMin || savePrice.isPending}>
                {savePrice.isPending ? 'Saving…' : 'Save'}
              </Button>
            )}
          </div>
          {minPrice > 0 && (
            <p className={`mt-1.5 text-[11px] ${belowMin ? 'text-red-600' : 'text-slate-400'}`}>
              Minimum set by Prospera: {usd(minPrice)}.{belowMin ? ' Price must be at or above this.' : ''}
            </p>
          )}
        </CardBody>
      </Card>

      {/* Billing */}
      <Card>
        <CardBody>
          <h3 className="text-sm font-semibold text-slate-900">Billing</h3>
          {settings && locked ? (
            // Locked: RadioGroup has no read-only mode, so show the chosen mode.
            <p className="mt-1 text-sm text-slate-800" data-testid="getlabs-billing-mode">
              {BILLING_MODE_LABELS[settings.billingMode] ?? settings.billingMode}
            </p>
          ) : settings ? (
            <RadioGroup
              name="getlabs-billing"
              options={billingOptions}
              value={settings.billingMode}
              onChange={(v) => saveMode.mutate(v)}
            />
          ) : (
            <Spinner size="sm" />
          )}
          <p className="mt-2 text-[11px] text-slate-400">
            “Charge the patient” collects the fee from the patient at order time. “Log &amp; settle monthly” logs
            the request; the account reconciles the payout to GetLabs for executed visits.
          </p>
        </CardBody>
      </Card>

      {/* Covered-by-membership (per-account entitlement; the account, not Prospera, covers the cost) */}
      <Card>
        <CardBody>
          <h3 className="text-sm font-semibold text-slate-900">Covered by membership</h3>
          <p className="text-[11px] text-slate-400 mb-2">
            Patients with an active membership selected here have their GetLabs visit covered by the account —
            the patient isn't charged, regardless of the billing mode above. The account still owes the vendor
            for the visit.
          </p>
          <SearchableMultiSelect
            options={membershipOptions}
            selectedValues={entitledLocal}
            onSelectionChange={(values) => {
              if (locked) return;
              setEntitledLocal(values);
              saveEntitled.mutate(values.map((v) => Number(v)));
            }}
            placeholder="No memberships — none will be covered"
            emptyMessage="No membership templates"
          />

          {entitledLocal.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {entitledLocal.map((val) => {
                const opt = membershipOptions.find((o) => o.value === val);
                const label = opt?.label ?? `#${val}`;
                return (
                  <span
                    key={val}
                    className="inline-flex items-center gap-1 rounded-full border border-indigo-200 bg-indigo-50 px-2 py-0.5 text-xs font-medium text-indigo-700"
                  >
                    {label}
                    {!locked && (
                    <button
                      type="button"
                      aria-label={`Remove ${label}`}
                      onClick={() => {
                        const next = entitledLocal.filter((v) => v !== val);
                        setEntitledLocal(next);
                        saveEntitled.mutate(next.map((v) => Number(v)));
                      }}
                      className="inline-flex h-3.5 w-3.5 items-center justify-center rounded-full text-indigo-500 hover:bg-indigo-100 hover:text-indigo-700"
                    >
                      <X className="h-2.5 w-2.5" />
                    </button>
                    )}
                  </span>
                );
              })}
            </div>
          )}
        </CardBody>
      </Card>
      </AdminLockedFieldset>

      {/* Reconciliation */}
      <Card>
        <CardBody>
          <div className="mb-2 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-slate-900">Month-end reconciliation</h3>
            {recon && <Badge variant="info">{recon.upcomingVisits} upcoming</Badge>}
          </div>
          {reconLoading ? (
            <div className="flex justify-center py-8"><Spinner size="sm" /></div>
          ) : (
            <>
              <div className="mb-3 grid grid-cols-3 gap-3 text-center">
                <Stat label="Visits (6 mo)" value={String(totals.visits)} />
                <Stat label="Owed to GetLabs" value={usd(totals.owed)} />
                <Stat label="Collected" value={usd(totals.collected)} />
              </div>
              <div className="h-56 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={recon?.months ?? []} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip formatter={(v: number) => usd(v)} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Bar dataKey="owedToVendor" name="Owed to GetLabs" fill="#6366f1" radius={[3, 3, 0, 0]} />
                    <Bar dataKey="collected" name="Collected" fill="#22c55e" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              {recon && recon.unitCost === 0 && (
                <p className="mt-2 text-[11px] text-amber-600">
                  Set <code>GETLABS_VISIT_COST</code> to populate the "owed" amounts.
                </p>
              )}
            </>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-slate-200 bg-slate-50 px-2 py-2">
      <div className="text-sm font-semibold text-slate-900">{value}</div>
      <div className="text-[10px] uppercase tracking-wide text-slate-400">{label}</div>
    </div>
  );
}
